import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import publicAxios from "../utils/publicAxios";
import {
  loginURL,
  onboardingURL,
  logoutURL,
  onboardConnectedURL,
  getOnboardDisconnectedURL,
  getLastConnectedSoftware
} from "../utils/constants/apiEndPoints";
import { showToast } from "../utils/utils";
import {
  RECAPTCHA_ERROR_TAG,
  showToastType
} from "../utils/constants/appConstants";
import { commonErrMsg, login } from "../locales/en";
import privateAxios from "../utils/axiosPrivate";
import { setAuthUser, setAuthUserLoading } from "./authSlice";
import { clearHubspotUTK } from "../utils/hubspotUtils";
const initialState = {
  onboardingData: {},
  loading: false,
  error: null,
  loginErrorMessage: "",
  disconnectConnectedSoftwareData: null,
  getConnectedSoftwareData: null
};
const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setOnboardingData(state, action) {
      state.onboardingData = action.payload;
    },
    setDisconnectConnectedSoftwareData(state, action) {
      state.disconnectConnectedSoftwareData = action.payload;
    },
    setGetConnectedSoftwareData(state, action) {
      state.getConnectedSoftwareData = action.payload;
    },
    clearLoginErrorMessage(state) {
      state.loginErrorMessage = "";
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userLogin.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(userLogin.rejected, (state, action) => {
        state.loading = false;
        const status = action.payload?.status;
        if (status) {
          const data = action.payload.data;
          if (status === 400) {
            const apiErrorsObj = data.errors?.[0];
            const apiErrors =
              apiErrorsObj && typeof apiErrorsObj === "object"
                ? Object.values(apiErrorsObj)[0]?.[0]
                : apiErrorsObj;
            if (apiErrors === RECAPTCHA_ERROR_TAG) {
              // `errors[0]` here is the bare tag "ReCAPTCHA Error", which tells
              // the user nothing. Name the remedy the screen offers instead.
              state.loginErrorMessage = {
                type: "TEXT",
                message: login?.errorMSG.recaptchaRejected
              };
            } else {
              state.loginErrorMessage = { type: "TEXT", message: apiErrors };
            }
          } else if (status === 403) {
            state.loginErrorMessage = {
              type: "TEXT",
              message: login?.errorMSG.profileWarning
            };
          } else if (status === 401) {
            const loginErrorMessage = data.message;
            if (loginErrorMessage.includes(commonErrMsg.item18)) {
              state.loginErrorMessage = {
                type: "CONTACT_SUPPORT",
                message: loginErrorMessage.replace(
                  /If urgent, please contact us/i,
                  ""
                )
              };
            } else {
              state.loginErrorMessage = {
                type: "TEXT",
                message: data.message
              };
            }
          } else {
            state.loginErrorMessage = { type: "TEXT", message: data.message };
          }
        } else {
          state.loginErrorMessage = {
            type: "TEXT",
            message: login?.errorMSG.somethingWrong
          };
        }
      })

      .addCase(singleConnectedSoftware.rejected, (_, action) => {
        const message = action.payload?.data?.message || commonErrMsg.item2;
        showToast(message, showToastType.error);
      })

      .addCase(getConnectedSoftware.fulfilled, (state, action) => {
        state.getConnectedSoftwareData = action.payload.data.data;
      });
  }
});

// Async thunk for login
export const userLogin = createAsyncThunk(
  "login/loginUser",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await publicAxios.post(loginURL, payload);
      if (response.status === 200) {
        dispatch(setAuthUserLoading(false));
        dispatch(setAuthUser(true));
        dispatch(setOnboardingData({}));
        dispatch(setDisconnectConnectedSoftwareData(null));
        dispatch(setGetConnectedSoftwareData({}));
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// Async thunk for logout
export const userLogout = createAsyncThunk(
  "login/logoutUser",
  async (payload, { dispatch }) => {
    try {
      const response = await privateAxios.post(logoutURL, payload);
      if (response.status === 200) {
        dispatch(setAuthUser(false));
        localStorage.clear();
        clearHubspotUTK();
        const channel = new BroadcastChannel("auth");
        channel.postMessage("logout");
        channel.close();
        window.location.href = "/login";
      }
    } catch {
      // handle catch here;
    }
  }
);

// Async thunk to get onboarding data
export const getOnboardingData = createAsyncThunk(
  "login/onboardingData",
  async (payload, { dispatch }) => {
    try {
      const response = await privateAxios.get(onboardingURL, {
        params: payload
      });
      dispatch(setOnboardingData(response.data.data));
      return response;
    } catch {
      showToast(commonErrMsg.item2, showToastType.error);
    }
  }
);

// Async thunk to check if software is connected
export const getConnectedSoftware = createAsyncThunk(
  "login/checkConnectedSoftware",
  async (payload) => {
    try {
      const response = await privateAxios.post(
        onboardConnectedURL,
        {},
        {
          params: payload
        }
      );
      return response;
    } catch {
      return false;
    }
  }
);

// Async thunk to check if software is connected
export const disconnectConnectedSoftware = createAsyncThunk(
  "login/disconnectConnectedSoftware",
  async (payload) => {
    try {
      const response = await privateAxios.post(
        getOnboardDisconnectedURL(payload)
      );
      return response;
    } catch {
      showToast(commonErrMsg.item2, showToastType.error);
      return false;
    }
  }
);

export const singleConnectedSoftware = createAsyncThunk(
  "login/singleConnectedSoftware",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getLastConnectedSoftware);
      dispatch(setDisconnectConnectedSoftwareData(response.data.data));
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const {
  setOnboardingData,
  setDisconnectConnectedSoftwareData,
  setGetConnectedSoftwareData,
  clearLoginErrorMessage
} = loginSlice.actions;
export default loginSlice.reducer;
